import { registerView, ViewComponent } from "@frui.ts/views";
import Tooltip from "controls/tooltip";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Card, Col, Row } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { getTimeZoneLabel } from "utils/dateUtils";
import OrderRecapitulationViewModel, { EditStep } from "viewModels/eshop/order/components/orderRecapitulationViewModel";

const OrderRecapitulationView: ViewComponent<OrderRecapitulationViewModel> = observer(({ vm }) => {
  const renderDateWithTimeZone = (date: Date) => {
    if (!date) {
      return vm.emptyState;
    }
    const formatted = vm.localizationService.formatDateTime(date);
    return !vm.isDifferentTimeZone ? (
      <span>{formatted}</span>
    ) : (
      <>
        <span>{formatted}</span>
        <Tooltip
          id="cet-tooltip"
          placement={isMobile ? "bottom" : "right"}
          tooltip={vm.localizationService.translateGeneral("timezoneTooltip")}
          trigger={["hover", "focus"]}>
          <span style={{ marginLeft: 4, textDecoration: "underline", textDecorationStyle: "dotted" }}>
            {getTimeZoneLabel(date)}
          </span>
        </Tooltip>
      </>
    );
  };

  const EditIconSvg = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.4397 2.18294C17.5448 2.28838 17.6038 2.43119 17.6038 2.58007C17.6038 2.72895 17.5448 2.87175 17.4397 2.97719L16.2663 4.15169L14.0163 1.90169L15.1897 0.727192C15.2952 0.62174 15.4382 0.5625 15.5874 0.5625C15.7365 0.5625 15.8796 0.62174 15.985 0.727192L17.4397 2.18182V2.18294ZM15.4709 4.94594L13.2209 2.69594L5.5563 10.3617C5.49438 10.4236 5.44777 10.4991 5.42017 10.5822L4.51455 13.2979C4.49813 13.3475 4.49579 13.4006 4.50781 13.4513C4.51984 13.5021 4.54574 13.5485 4.58262 13.5854C4.61951 13.6223 4.66592 13.6482 4.71668 13.6602C4.76744 13.6722 4.82054 13.6699 4.87005 13.6534L7.5858 12.7478C7.66878 12.7205 7.74428 12.6743 7.8063 12.6128L15.4709 4.94594Z"
        fill="#51535D"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.125 15.1875C1.125 15.6351 1.30279 16.0643 1.61926 16.3807C1.93572 16.6972 2.36495 16.875 2.8125 16.875H15.1875C15.6351 16.875 16.0643 16.6972 16.3807 16.3807C16.6972 16.0643 16.875 15.6351 16.875 15.1875V8.4375C16.875 8.28832 16.8157 8.14524 16.7102 8.03975C16.6048 7.93426 16.4617 7.875 16.3125 7.875C16.1633 7.875 16.0202 7.93426 15.9148 8.03975C15.8093 8.14524 15.75 8.28832 15.75 8.4375V15.1875C15.75 15.3367 15.6907 15.4798 15.5852 15.5852C15.4798 15.6907 15.3367 15.75 15.1875 15.75H2.8125C2.66332 15.75 2.52024 15.6907 2.41475 15.5852C2.30926 15.4798 2.25 15.3367 2.25 15.1875V2.8125C2.25 2.66332 2.30926 2.52024 2.41475 2.41475C2.52024 2.30926 2.66332 2.25 2.8125 2.25H10.125C10.2742 2.25 10.4173 2.19074 10.5227 2.08525C10.6282 1.97976 10.6875 1.83668 10.6875 1.6875C10.6875 1.53832 10.6282 1.39524 10.5227 1.28975C10.4173 1.18426 10.2742 1.125 10.125 1.125H2.8125C2.36495 1.125 1.93572 1.30279 1.61926 1.61926C1.30279 1.93572 1.125 2.36495 1.125 2.8125V15.1875Z"
        fill="#51535D"
      />
    </svg>
  );

  const EditableValue = ({ step, children }: { step: EditStep; children: React.ReactNode }) => (
    <Row className="justify-content-end align-items-center">
      <div
        className="d-flex align-items-center"
        onClick={isMobile ? () => vm.navigateToStep(step) : undefined}
        style={isMobile ? { cursor: "pointer" } : undefined}
        role={isMobile ? "button" : undefined}
        aria-label={isMobile ? vm.translate("edit") : undefined}>
        <div className="font-weight-bold">{children}</div>
        <span
          onClick={!isMobile ? () => vm.navigateToStep(step) : undefined}
          style={!isMobile ? { cursor: "pointer", marginLeft: 8 } : { marginLeft: 8 }}
          role={!isMobile ? "button" : undefined}
          aria-label={!isMobile ? vm.translate("edit") : undefined}>
          <EditIconSvg />
        </span>
      </div>
    </Row>
  );

  const StaticValue = ({ children }: { children: React.ReactNode }) => (
    <Row className="justify-content-end">
      <div className="font-weight-bold">{children}</div>
    </Row>
  );

  const ValueDisplay = ({ step, children }: { step: EditStep; children: React.ReactNode }) => {
    if (step === "fuelType" && !vm.canEditFuelType) {
      return <StaticValue>{children}</StaticValue>;
    }
    return vm.isSmartRedirect ? <EditableValue step={step}>{children}</EditableValue> : <StaticValue>{children}</StaticValue>;
  };

  return (
    <Card className="bg-transparent mb-6 border-0 text-break">
      <Row className="d-flex align-items-center justify-content-between px-3 px-md-0 mx-sm-1 mt-3">
        <Col xs="auto">
          <Row>
            <div className="mb-0 text-wrap">{vm.translate("vehicle")}</div>
          </Row>
        </Col>
        <Col xs="auto" className="flex-grow-1 text-right">
          <ValueDisplay step="licensePlate">{vm.vehicleSummaryInfo}</ValueDisplay>
        </Col>
      </Row>
      <Row className="d-flex align-items-center justify-content-between px-3 px-md-0 mx-sm-1 mt-3">
        <Col xs="auto">
          <Row>
            <div className="mb-0 text-wrap">{vm.translate("markType")}</div>
          </Row>
        </Col>
        <Col xs="auto" className="flex-grow-1 text-right">
          <ValueDisplay step="chargeType">{vm.markSummaryInfo}</ValueDisplay>
        </Col>
      </Row>
      <Row className="d-flex align-items-center justify-content-between px-3 px-md-0 mx-sm-1 mt-3">
        <Col xs="auto">
          <Row>
            <div className="mb-0 text-wrap">{vm.translate("fuelType")}</div>
          </Row>
        </Col>
        <Col xs="auto" className="flex-grow-1 text-right">
          <ValueDisplay step="fuelType">{vm.fuelTypeInfo}</ValueDisplay>
        </Col>
      </Row>
      <Row className="d-flex align-items-center justify-content-between px-3 px-md-0 mx-sm-1 mt-3">
        <Col xs="auto">
          <Row>
            <div className="mb-0 text-wrap">{vm.translate("validSince")}</div>
          </Row>
        </Col>
        <Col xs="auto" className="flex-grow-1 text-right">
          <ValueDisplay step="validitySince">
            {vm.validitySinceInfo ? renderDateWithTimeZone(new Date(vm.validitySinceInfo)) : vm.emptyState}
          </ValueDisplay>
        </Col>
      </Row>
      <Row className="d-flex align-items-center justify-content-between px-3 px-md-0 mx-sm-1 mt-3">
        <Col xs="auto">
          <Row>
            <div className="mb-0 text-wrap">{vm.translate("validUntil")}</div>
          </Row>
        </Col>
        <Col xs="auto" className="flex-grow-1 text-right">
          <Row className="justify-content-end align-items-center">
            <div className="font-weight-bold">
              {vm.validityUntilInfo ? renderDateWithTimeZone(new Date(vm.validityUntilInfo)) : vm.emptyState}
            </div>
          </Row>
        </Col>
      </Row>
      <Row className="d-flex align-items-center justify-content-between px-3 px-md-0 mx-sm-1 mt-3 pb-4">
        <Col xs="auto">
          <Row>
            <div className="mb-0 text-wrap">{vm.translate("email")}</div>
          </Row>
        </Col>
        <Col xs="auto" className="flex-grow-1 text-right">
          <ValueDisplay step="contact">{vm.emailInfo}</ValueDisplay>
        </Col>
      </Row>
      <Row
        className="d-flex align-items-center justify-content-between px-sm-2 px-md-0 py-4 mx-0"
        style={{ borderBottom: "1px solid #E4E8EC", borderTop: "1px solid #E4E8EC" }}>
        <Col xs="auto">
          <Row>
            <h4 className="mb-0 text-wrap">{vm.translate("price")}</h4>
          </Row>
        </Col>
        <Col xs="auto">
          <Row>
            <h4 className="mb-0 text-wrap">{vm.priceSummaryInfo}</h4>
          </Row>
        </Col>
      </Row>
    </Card>
  );
});

registerView(OrderRecapitulationView, OrderRecapitulationViewModel);
